import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetData } from '../services/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { useCountryDefaultLanguage } from './useCountryDefaultLanguage';
import { getCurrentLanguage } from '../../features/Settings/utils/languageUtils';
import { isFreshForSession, useSessionStartedAt } from './useSessionStartedAt';

type ProfileWithPreferences = {
  preferences?: { language?: string };
};

export type LanguagePreferenceState = {
  isSettled: boolean;
};

export const useLanguagePreference = (): LanguagePreferenceState => {
  const { isFullyAuthenticated, flags } = useAuth();
  const { i18n } = useTranslation();
  const baseUrl = import.meta.env.VITE_FOUNDATION_API_BASE_URL as string;
  const hasResolved = useRef(false);

  const countryDefaultEnabled = flags?.ENABLE_COUNTRY_DEFAULT_LANGUAGE === true;

  const { data, isSuccess, isError, dataUpdatedAt, errorUpdatedAt } =
    useGetData<ProfileWithPreferences>(
      { url: `${baseUrl}/v1.0/profile`, queryKey: 'languagePreference' },
      { enabled: isFullyAuthenticated },
    );

  const country = useCountryDefaultLanguage();
  const sessionStartedAt = useSessionStartedAt(isFullyAuthenticated);

  const profileFresh =
    (isSuccess && isFreshForSession(dataUpdatedAt, sessionStartedAt)) ||
    (isError && isFreshForSession(errorUpdatedAt, sessionStartedAt));
  const profileSettled = !isFullyAuthenticated || profileFresh;
  const isSettled = profileSettled && country.isSettled;

  useEffect(() => {
    const savedLanguage = data?.preferences?.language;

    if (!countryDefaultEnabled) {
      if (savedLanguage != null) {
        i18n.changeLanguage(savedLanguage);
      }
      return;
    }

    if (!isFullyAuthenticated) {
      hasResolved.current = false;
      return;
    }

    if (hasResolved.current || !isSettled) return;
    hasResolved.current = true;

    if (savedLanguage != null && savedLanguage !== '') {
      i18n.changeLanguage(savedLanguage);
      return;
    }

    if (country.language != null) {
      i18n.changeLanguage(country.language);
      return;
    }

    const browserLanguage = typeof navigator === 'undefined' ? undefined : navigator.language;
    i18n.changeLanguage(getCurrentLanguage(browserLanguage?.split('-')[0]));
  }, [countryDefaultEnabled, isFullyAuthenticated, isSettled, data, country.language, i18n]);

  return { isSettled: countryDefaultEnabled ? isSettled : true };
};
